import React, { useEffect, useMemo, useState } from 'react';
import { Digit, Reel, SlotMachine, StaticValue, Strip } from './SlotMachineValue.styles';

export interface SlotMachineValueProps {
  /** 최종 표시 값 (예: "20") */
  value: string;
  /** true일 때 슬롯 롤링 시작 */
  active?: boolean;
}

interface DigitReelProps {
  target: number;
  active: boolean;
  delayMs: number;
}

function DigitReel({ target, active, delayMs }: DigitReelProps) {
  const cycles = 4;
  const strip = useMemo(
    () => Array.from({ length: cycles * 10 + target + 1 }, (_, index) => index % 10),
    [cycles, target],
  );
  const finalIndex = cycles * 10 + target;
  const [spinning, setSpinning] = useState(false);

  useEffect(() => {
    if (!active) {
      setSpinning(false);
      return;
    }

    setSpinning(false);
    const startTimer = window.setTimeout(() => setSpinning(true), delayMs);
    return () => window.clearTimeout(startTimer);
  }, [active, delayMs, target]);

  return (
    <Reel aria-hidden="true">
      <Strip
        $spinning={spinning}
        style={{ '--reel-offset': finalIndex } as React.CSSProperties}
      >
        {strip.map((digit, index) => (
          <Digit key={index}>{digit}</Digit>
        ))}
      </Strip>
    </Reel>
  );
}

/**
 * 슬롯머신처럼 숫자가 굴러가며 최종 값에 멈춘다.
 */
export function SlotMachineValue({ value, active = false }: SlotMachineValueProps) {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    setReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  const digits = value.split('').map((char) => Number.parseInt(char, 10));

  if (reducedMotion || !active || digits.some((digit) => Number.isNaN(digit))) {
    return <StaticValue>{value}</StaticValue>;
  }

  return (
    <SlotMachine aria-label={value}>
      {digits.map((digit, index) => (
        <DigitReel key={`${index}-${digit}`} target={digit} active={active} delayMs={index * 120} />
      ))}
    </SlotMachine>
  );
}
